import React from "react";
import { History } from "lucide-react";

// Read-only in-session reference: the last time the lifter performed THIS
// exercise (matched by exercise_id, so it survives program swaps or advanced
// substitutions). Deliberately shows numbers only — no tap-to-prefill —
// because Hypergains programming leans on double progression AND back-off
// sets, both of which are undermined if the top-set weight is auto-populated
// (see /education/back-off-sets and /education/double-progression).
//
// Props:
//   sessions: [{ date, sets, top_weight, top_reps }, ...] ordered newest → oldest
//   unilateral: boolean — changes the per-set string layout
export default function LastTimeChip({ sessions, unilateral, testIdPrefix = "last-time" }) {
    if (!Array.isArray(sessions) || sessions.length === 0) {
        return (
            <div
                className="mb-3 rounded-sm border border-dashed border-border/70 bg-secondary/30 px-3 py-2 text-xs text-muted-foreground"
                data-testid={`${testIdPrefix}-empty`}
            >
                <span className="font-bold uppercase tracking-wider text-foreground/70">First time</span>
                {" — pick a weight you can do for ~6 clean reps at RIR 1–2."}
            </div>
        );
    }

    const latest = sessions[0];
    const dateStr = formatShortDate(latest.date);
    const setSummary = summarizeSets(latest.sets, unilateral);

    // Sparkline: top-set weight per session, ordered oldest → newest so the
    // most recent bar sits on the right (natural reading direction).
    const trendPoints = sessions
        .slice()
        .reverse()
        .map((s) => ({ w: Number(s.top_weight) || 0, r: Number(s.top_reps) || 0, d: s.date }));
    const maxW = Math.max(...trendPoints.map((p) => p.w), 1);

    return (
        <div
            className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-sm border border-border bg-secondary/40 px-3 py-2 text-xs"
            data-testid={`${testIdPrefix}-chip`}
        >
            <div className="flex items-center gap-1.5 label-eyebrow text-muted-foreground">
                <History className="h-3 w-3" />
                Last time · {dateStr}
            </div>
            <div className="tabular-nums text-foreground/90" data-testid={`${testIdPrefix}-sets`}>
                {setSummary}
            </div>
            {trendPoints.length >= 2 && (
                <div className="ml-auto flex items-end gap-0.5" data-testid={`${testIdPrefix}-sparkline`} aria-label="Top-set weight trend">
                    {trendPoints.map((p, i) => {
                        const heightPct = maxW > 0 ? Math.max(15, Math.round((p.w / maxW) * 100)) : 15;
                        const isLatest = i === trendPoints.length - 1;
                        return (
                            <div
                                key={i}
                                className={`w-1.5 ${isLatest ? "bg-accent-red" : "bg-muted-foreground/40"}`}
                                style={{ height: `${(heightPct / 100) * 24}px` }}
                                title={`${p.w} × ${p.r}`}
                            />
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function formatShortDate(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    } catch {
        return "—";
    }
}

// "60 × 8 · 8 · 7"  — same weight across sets, dot-separated rep counts.
// "60×8 · 62.5×7 · 62.5×6" — weight varied set-to-set (e.g. back-off set).
// Unilateral rows collapse to `L/R weight × reps` per set.
function summarizeSets(sets, unilateral) {
    if (!Array.isArray(sets) || sets.length === 0) return "—";
    if (unilateral) {
        return sets
            .map((s) => {
                const wl = s.weight_left ?? s.weight ?? 0;
                const wr = s.weight ?? 0;
                const rl = s.reps_left ?? s.reps ?? 0;
                const rr = s.reps ?? 0;
                if (wl === wr && rl === rr) return `${wl}×${rl}`;
                return `L ${wl}×${rl} / R ${wr}×${rr}`;
            })
            .join(" · ");
    }
    const weights = sets.map((s) => Number(s.weight) || 0);
    const allSameWeight = weights.every((w) => w === weights[0]);
    if (allSameWeight) {
        return `${weights[0]} × ${sets.map((s) => s.reps ?? 0).join(" · ")}`;
    }
    return sets.map((s) => `${s.weight ?? 0}×${s.reps ?? 0}`).join(" · ");
}
